import React, { useState, useEffect, useRef } from 'react';

const MONO_FONT = "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Courier New', monospace";
const TYPING_SPEED = 20;
const ROW_DELAY = 100;

function MiniCrtTable({ games, onClick }) {
  const [headerRow, setHeaderRow] = useState(null);
  const [typedRows, setTypedRows] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [typingPhase, setTypingPhase] = useState('idle');
  const animRef = useRef(null);

  useEffect(() => {
    const HEADERS = ['GAME', 'TYPE', 'PLAYERS'];

    if (!games || games.length === 0) {
      setHeaderRow({ cols: HEADERS, activeCol: 2 });
      setTypedRows([]);
      setIsComplete(true);
      setTypingPhase('done');
      return;
    }

    setHeaderRow(null);
    setTypedRows([]);
    setIsComplete(false);
    setTypingPhase('header');

    const typeLabel = (t) => t === 'FI' ? 'Find The Image' : 'Creative Coaster';
    const dataRows = games.map(g => {
      let name = g.name.toUpperCase();
      if (name.length > 22) name = name.slice(0, 21) + '..';
      return [name, typeLabel(g.type), String(g.players)];
    });

    const headerFull = HEADERS.join('  ');
    let phase = 'header';
    let charIdx = 0;
    let rowIdx = 0;
    const built = [];

    const tick = () => {
      if (phase === 'header') {
        charIdx++;
        const partial = headerFull.slice(0, charIdx);
        setHeaderRow(splitRow(partial, HEADERS, { trim: true }));
        setTypingPhase('header');
        if (charIdx >= headerFull.length) {
          phase = 'rows';
          charIdx = 0;
          rowIdx = 0;
          animRef.current = setTimeout(tick, ROW_DELAY);
        } else {
          animRef.current = setTimeout(tick, TYPING_SPEED);
        }
      } else {
        if (rowIdx >= dataRows.length) {
          setIsComplete(true);
          setTypingPhase('done');
          return;
        }
        const fullRow = dataRows[rowIdx];
        const fullText = fullRow.join('  ');
        if (charIdx === 0) {
          built.unshift({ cols: ['', '', ''], activeCol: 0, typing: true });
        }
        charIdx++;
        const partial = fullText.slice(0, charIdx);
        built[0] = { ...splitRow(partial, fullRow), typing: true };
        if (built.length > 1) built[1] = { ...built[1], typing: false };
        setTypedRows([...built]);
        setTypingPhase('rows');

        if (charIdx >= fullText.length) {
          charIdx = 0;
          rowIdx++;
          animRef.current = setTimeout(tick, ROW_DELAY);
        } else {
          animRef.current = setTimeout(tick, TYPING_SPEED);
        }
      }
    };

    animRef.current = setTimeout(tick, 300);
    return () => { if (animRef.current) clearTimeout(animRef.current); };
  }, [games]);

  return (
    <div
      style={{
        flex: 1, minHeight: 0,
        background: '#0a1628',
        borderRadius: '8px',
        border: '1px solid rgba(215, 2, 246, 0.25)',
        padding: '0.5rem 0.6rem',
        fontFamily: MONO_FONT,
        fontSize: '0.8rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(215, 2, 246, 0.5)';
        e.currentTarget.style.boxShadow = '0 0 12px rgba(215, 2, 246, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(215, 2, 246, 0.25)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header row */}
      <div style={{
        display: 'flex',
        color: '#D702F6', letterSpacing: '0.04em',
        paddingBottom: '0.3rem',
        borderBottom: '1px dashed rgba(215, 2, 246, 0.3)',
        marginBottom: '0.3rem',
        minHeight: '1.4em'
      }}>
        {headerRow ? (
          headerRow.cols.map((text, ci) => (
            <span
              key={ci}
              className={caretClass(
                typingPhase === 'header' && !isComplete && headerRow.activeCol === ci,
                false
              )}
              style={{ flex: ci === 2 ? 1 : 2, textAlign: 'left' }}
            >
              {text}
            </span>
          ))
        ) : (
          <span className="mini-crt-caret mini-crt-caret--blink" />
        )}
      </div>

      {/* Data rows */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {games.length === 0 ? (
          <div style={{ color: 'rgba(142, 200, 248, 0.4)', padding: '0.25rem 0', fontStyle: 'italic' }}>
            No games yet...
          </div>
        ) : (
          typedRows.map((row, i) => {
            // The caret only ever lives on the newest row, and within that row it
            // sits in whichever column is currently receiving characters.
            const showCaret = i === 0 && (isComplete || row.typing);
            return (
              <div key={i} style={{
                display: 'flex',
                color: '#8ec8f8',
                padding: '0.15rem 0',
                lineHeight: 1.5
              }}>
                {row.cols.map((text, ci) => (
                  <span
                    key={ci}
                    className={caretClass(showCaret && row.activeCol === ci, isComplete)}
                    style={{ flex: ci === 2 ? 1 : 2, textAlign: 'left' }}
                  >
                    {text}
                  </span>
                ))}
              </div>
            );
          })
        )}
      </div>

      <div style={{
        textAlign: 'center', color: 'rgba(215, 2, 246, 0.6)',
        fontSize: '0.75rem', letterSpacing: '0.05em',
        paddingTop: '0.3rem',
        borderTop: '1px dashed rgba(215, 2, 246, 0.15)'
      }}>
        VIEW ALL GAMES
      </div>

      <style>{`
        /* The caret is a ::after on the live text span, so it always sits flush
           against the last typed character instead of at a fixed position. */
        .mini-crt-caret::after {
          content: '\\2588';
          color: inherit;
        }
        .mini-crt-caret--blink::after {
          animation: mini-crt-blink 0.7s step-end infinite;
        }
        @keyframes mini-crt-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Which column the caret belongs to, plus the class names that draw it.
function caretClass(show, blink) {
  if (!show) return undefined;
  return blink ? 'mini-crt-caret mini-crt-caret--blink' : 'mini-crt-caret';
}

// Splits the partially-typed row into its three columns and reports which
// column the insertion point currently sits in, so the caret can follow it.
function splitRow(partial, fullCols, { trim = false } = {}) {
  const full = fullCols.join('  ');
  const visible = full.slice(0, partial.length);
  const sep1 = fullCols[0].length;
  const sep2 = sep1 + 2 + fullCols[1].length;
  const clean = (s) => (trim ? s.trimEnd() : s);

  const col1 = clean(visible.slice(0, Math.min(partial.length, sep1)));
  const col2 = partial.length > sep1 + 2 ? clean(visible.slice(sep1 + 2, Math.min(partial.length, sep2))) : '';
  const col3 = partial.length > sep2 + 2 ? clean(visible.slice(sep2 + 2)) : '';

  // While the two-space gap between columns is being "typed" the caret stays at
  // the end of the column it just finished.
  let activeCol = 0;
  if (partial.length > sep2 + 2) activeCol = 2;
  else if (partial.length > sep1 + 2) activeCol = 1;

  return { cols: [col1, col2, col3], activeCol };
}

export default MiniCrtTable;
